import AffiliateDashboard from 'src/components/affiliate/AffiliateDashboard'
import ApplyForm, { ProgramPitch } from 'src/components/affiliate/ApplyForm'
import { AffiliateStatusBadge, errorMessage } from 'src/components/affiliate/labels'
import TermsGate from 'src/components/affiliate/TermsGate'
import { useConnectReturn } from 'src/components/affiliate/ConnectBanner'
import {
    Banner,
    Card,
    CardTitle,
    ErrorText,
    InlineSpinner,
    Muted,
    Narrow,
    Page,
    PageSubtitle,
    PageTitle,
    Row,
} from 'src/components/affiliate/ui'
import { isActiveOrPaused, useAffiliateMe, usePublicProgram } from 'src/hooks/useAffiliate'
import { formatDay } from 'src/utils/affiliateFormat'

/** /affiliate: pitch and application for non-affiliates, status notices, terms gate, or the dashboard. */
const AffiliatePage = () => {
    const me = useAffiliateMe()
    const program = usePublicProgram()
    useConnectReturn(me.data)

    const affiliate = me.data

    let body
    if (me.isPending) {
        body = <InlineSpinner />
    } else if (me.isError) {
        body = <ErrorText>{errorMessage(me.error)}</ErrorText>
    } else if (!affiliate) {
        body = program.data ? (
            <>
                <ProgramPitch program={program.data} />
                {program.data.applicationsOpen ? (
                    <ApplyForm program={program.data} />
                ) : (
                    <Card>
                        <CardTitle>Applications are closed</CardTitle>
                        <Muted>We're not taking new affiliates right now. Check back soon.</Muted>
                    </Card>
                )}
            </>
        ) : program.isError ? (
            <ErrorText>{errorMessage(program.error)}</ErrorText>
        ) : (
            <InlineSpinner />
        )
    } else if (affiliate.status === 'applied') {
        body = (
            <Card>
                <CardTitle>Your application is under review</CardTitle>
                <Muted>
                    Thanks for applying on {formatDay(affiliate.createdAt)}. We'll update this page once it's been
                    reviewed.
                </Muted>
            </Card>
        )
    } else if (affiliate.status === 'rejected') {
        const reapplyAt = affiliate.reapplyAvailableAt ? new Date(affiliate.reapplyAvailableAt) : null
        const canReapply = !reapplyAt || reapplyAt <= new Date()
        body = (
            <>
                <Card>
                    <CardTitle>Your application wasn't approved</CardTitle>
                    <Muted>
                        {canReapply
                            ? 'You can apply again below.'
                            : `You can re-apply on ${formatDay(affiliate.reapplyAvailableAt)}.`}
                    </Muted>
                </Card>
                {canReapply && program.data?.applicationsOpen && <ApplyForm program={program.data} />}
            </>
        )
    } else if (affiliate.status === 'terminated') {
        body = (
            <Card>
                <CardTitle>Your affiliate account has been closed</CardTitle>
                <Muted>You're no longer part of the affiliate program. Contact support if you have questions.</Muted>
            </Card>
        )
    } else if (isActiveOrPaused(affiliate) && affiliate.terms?.needsAcceptance) {
        body = <TermsGate me={affiliate} program={program.data} />
    } else {
        body = <AffiliateDashboard me={affiliate} />
    }

    const showDashboard = !!affiliate && isActiveOrPaused(affiliate) && !affiliate.terms?.needsAcceptance

    const header = (
        <div>
            <Row $gap={12}>
                <PageTitle>Affiliate</PageTitle>
                {/* Active is the normal state; only flag the unusual one */}
                {affiliate?.status === 'paused' && <AffiliateStatusBadge status="paused" />}
            </Row>
            {!showDashboard && <PageSubtitle>Earn from every SwiftSole subscriber you refer.</PageSubtitle>}
        </div>
    )

    // The dashboard needs the full width; everything else reads better as one centred column.
    return (
        <Page>
            {showDashboard ? (
                <>
                    {header}
                    {body}
                </>
            ) : (
                <Narrow>
                    {header}
                    {affiliate?.status === 'paused' && (
                        <Banner $tone="warn">
                            <p>
                                Your account is paused. You still earn on customers you've already referred, but new
                                referrals aren't credited and payouts are disabled.
                            </p>
                        </Banner>
                    )}
                    {body}
                </Narrow>
            )}
        </Page>
    )
}

export default AffiliatePage
